import { supabase } from '../lib/supabase';
import { ProvinciaEclesiastica } from './provinciaEclesiasticaService';
import { RELATIONS } from '../lib/foreignKeys';

export interface Diocesis {
  id: string;
  nombre: string;
  tipo: 'diocesis' | 'prefectura';
  provincia_eclesiastica_id: string;
  activo: boolean;
  created_at: string;
  provincia_eclesiastica?: ProvinciaEclesiastica;
  cantidad_lumisiales?: number;
}

export const diocesisService = {
  async listarActivas(): Promise<Diocesis[]> {
    const { data, error } = await supabase
      .from('diocesis')
      .select(RELATIONS.DIOCESIS_FULL)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async listarTodas(): Promise<Diocesis[]> {
    const { data, error } = await supabase
      .from('diocesis')
      .select(RELATIONS.DIOCESIS_FULL)
      .order('nombre', { ascending: true});

    if (error) throw error;
    return data || [];
  },

  async listarPorProvinciaEclesiastica(provinciaEclesiasticaId: string): Promise<Diocesis[]> {
    const { data, error } = await supabase
      .from('diocesis')
      .select(RELATIONS.DIOCESIS_FULL)
      .eq('provincia_eclesiastica_id', provinciaEclesiasticaId)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getByProvinciaEclesiastica(provinciaEclesiasticaId: string): Promise<Diocesis[]> {
    const { data, error } = await supabase
      .from('diocesis')
      .select(RELATIONS.DIOCESIS_FULL)
      .eq('provincia_eclesiastica_id', provinciaEclesiasticaId)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async listarPorTipo(tipo: 'diocesis' | 'prefectura'): Promise<Diocesis[]> {
    const { data, error } = await supabase
      .from('diocesis')
      .select(RELATIONS.DIOCESIS_FULL)
      .eq('tipo', tipo)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async obtenerPorId(id: string): Promise<Diocesis | null> {
    const { data, error } = await supabase
      .from('diocesis')
      .select(RELATIONS.DIOCESIS_FULL)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async obtenerConCantidadLumisiales(id: string): Promise<Diocesis | null> {
    const diocesis = await this.obtenerPorId(id);
    if (!diocesis) return null;

    const { count, error } = await supabase
      .from('lumisiales')
      .select('*', { count: 'exact', head: true })
      .eq('diocesis_id', id)
      .eq('activo', true);

    if (error) throw error;

    return {
      ...diocesis,
      cantidad_lumisiales: count || 0
    };
  },

  async listarConCantidadLumisiales(): Promise<Diocesis[]> {
    const diocesisList = await this.listarTodas();

    const diocesisConCantidad = await Promise.all(
      diocesisList.map(async (diocesis) => {
        const { count, error } = await supabase
          .from('lumisiales')
          .select('*', { count: 'exact', head: true })
          .eq('diocesis_id', diocesis.id)
          .eq('activo', true);

        if (error) {
          console.error(`Error contando lumisiales para diócesis ${diocesis.id}:`, error);
          return { ...diocesis, cantidad_lumisiales: 0 };
        }

        return {
          ...diocesis,
          cantidad_lumisiales: count || 0
        };
      })
    );

    return diocesisConCantidad;
  },

  async crear(diocesis: Omit<Diocesis, 'id' | 'created_at' | 'provincia_eclesiastica' | 'cantidad_lumisiales'>): Promise<Diocesis> {
    const { data, error } = await supabase
      .from('diocesis')
      .insert(diocesis)
      .select(RELATIONS.DIOCESIS_FULL)
      .single();

    if (error) throw error;
    return data;
  },

  async actualizar(id: string, diocesis: Partial<Omit<Diocesis, 'id' | 'created_at' | 'provincia_eclesiastica' | 'cantidad_lumisiales'>>): Promise<Diocesis> {
    const { data, error } = await supabase
      .from('diocesis')
      .update(diocesis)
      .eq('id', id)
      .select(RELATIONS.DIOCESIS_FULL)
      .single();

    if (error) throw error;
    return data;
  },

  async eliminar(id: string): Promise<void> {
    const { error } = await supabase
      .from('diocesis')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async desactivar(id: string): Promise<Diocesis> {
    return this.actualizar(id, { activo: false });
  },

  async activar(id: string): Promise<Diocesis> {
    return this.actualizar(id, { activo: true });
  }
};
